import { KeyRound, Mail, UserPlus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { Field } from "../components/Field.js";
import { BoardBackdrop } from "../components/BoardBackdrop.js";
import { LanguageSwitch } from "../components/LanguageSwitch.js";
import { UserMenu } from "../components/UserMenu.js";
import { Logo } from "../components/Logo.js";
import { Action, ErrorLine, LinkAction } from "../components/Screen.js";
import { SignInForm } from "../components/SignInForm.js";
import { useSession } from "../hooks/useSession.js";
import { useSignIn } from "../hooks/useSignIn.js";
import { signOut } from "../services/authService.js";
import type { SessionView } from "../services/http.js";
import { createParty, previewParty } from "../services/partyService.js";

/**
 * `/` — two different pages behind one route.
 *
 * Since phase 20a this is a switch and nothing else: an anonymous visitor gets
 * the [`Door`], somebody signed in gets the [`Landing`] this page used to be for
 * everybody. They are separate components rather than one with conditions
 * because they share no layout — the door is a `max-w-sm` column built around a
 * hero mark, the landing is a wide two-column board — and expressing both in
 * one tree is how a page ends up with a flag in every element.
 *
 * ⚠️ **The split is a product boundary, not a cosmetic one.** What the door may
 * say is decided by this being a private installation; see [`Door`].
 */
export function HomePage() {
  const { account, refresh } = useSession();

  // Three states, not two, and the reason is in `useSession`: rendering the
  // door while the session request is still in flight flashes "sign in" at a
  // host who is already signed in. A held frame is the cheaper wrong answer.
  if (account === undefined) return <Holding />;
  return account ? <Landing account={account} refresh={refresh} /> : <Door />;
}

/**
 * What an anonymous visitor gets: the mark, the name, and a way in.
 *
 * # Phase 20a — the door says nothing about the game
 *
 * Everything that described sonolume to a stranger used to be here: the
 * headline, the blurb, the three numbered steps, "first complete timeline
 * wins", and a code box inviting anybody to walk into a room. This is a
 * **private installation** (phase 19a, ADR 0021) where every account is
 * approved by hand, so a public page marketing it to strangers was describing a
 * product that does not exist.
 *
 * ⚠️ **The page was never the only copy of that description.** `web/index.html`
 * carried it in `<meta name="description">` and `public/manifest.json` in its
 * own, both served publicly, both reachable without rendering a thing — and
 * `public/robots.txt` explicitly `Allow`s `/$` and `/manifest.json`. Editing
 * this file alone would have moved the sentence, not removed it. If a fourth
 * copy is ever added, it belongs in that list.
 *
 * # Why it does not use `Screen`
 *
 * `Screen` is the frame the account pages share, and it is built around a
 * title: a heading, an optional lead under it, the form below. The door has no
 * title — it is the mark, the name, and the form — and its mark is hero-sized
 * where `Screen` deliberately shrinks the mark so it does not compete with the
 * task. Bending `Screen` to express both would put a flag in six screens to
 * serve one.
 */
function Door() {
  const { t } = useTranslation();
  const { submit, busy, error } = useSignIn();

  return (
    <div className="relative min-h-full">
      <BoardBackdrop />

      <div className="absolute right-4 top-4 z-20 flex items-center gap-2">
        <LanguageSwitch className="flex gap-1 rounded-lg border border-white/10 bg-stage-900/70 p-1 backdrop-blur" />
      </div>

      <main className="relative flex min-h-full flex-col items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <header className="flex flex-col items-center text-center">
            <Logo size={160} />
            {/* `h1` because on this page the name is what the page *is*. There
                is no other heading to compete with it. */}
            <h1 className="wordmark mt-5 text-3xl font-bold tracking-tight">
              {t("app.name")}
            </h1>
          </header>

          <div className="mt-10">
            <SignInForm onSubmit={submit} busy={busy} error={error} />
          </div>

          {/* Buttons, not a stack of fine print. Three 12px sentences under a
              form are a bad target on the one device most people open this on,
              and they read as small print rather than as the ways on from here.
              They stay anchors underneath — see `LinkAction`. */}
          <div className="mt-8 flex flex-col gap-2">
            {/* The two ways *in*, side by side and equally weighted — a person
                who has no account and a person who would rather not type a
                password are answering the same question. The passwordless path
                still exists; it is one screen back rather than a second panel
                here — it has its own screen. See `SignInLinkPage`. */}
            <div className="grid grid-cols-2 gap-2">
              <LinkAction to="/register" icon={UserPlus} accent>
                {t("login.createAccount")}
              </LinkAction>
              <LinkAction to="/signin" icon={Mail}>
                {t("home.door.useLink")}
              </LinkAction>
            </div>
            {/* Recovery is a different kind of thing — it is for somebody who
                already has an account and is stuck — so it sits under the pair
                rather than competing with them. */}
            <LinkAction to="/forgot" icon={KeyRound}>
              {t("login.forgot")}
            </LinkAction>
          </div>
        </div>
      </main>
    </div>
  );
}

/**
 * The frame with nothing in it, while the session request is in flight.
 *
 * The backdrop and nothing else, so the paint that follows is a page filling in
 * rather than a page replacing itself.
 */
function Holding() {
  return (
    <div className="relative min-h-full">
      <BoardBackdrop />
    </div>
  );
}

/**
 * The front door for somebody signed in — join a party, or start one.
 *
 * # It does not use `Screen`
 *
 * Every other screen outside a game is a `max-w-sm` column, and this one is a
 * wide two-column landing with the mark on it. Bending `Screen` into both would
 * make the frame six screens share depend on a flag only one of them sets.
 *
 * # The mode and the deck are not here
 *
 * They are in the lobby, where the host can still change them with people in
 * the room. A second mode picker on this screen would be a setting chosen
 * before anybody has arrived, and changed again ten seconds later.
 */
function Landing({ account, refresh }: { account: SessionView; refresh: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="relative min-h-full">
      <BoardBackdrop />

      {/* One row, top right. The user menu lives here rather than under the
          form it used to hide beneath — it is where every product on earth puts
          it, which is the whole argument. */}
      <div className="absolute right-4 top-4 z-20 flex items-center gap-2">
        <LanguageSwitch className="flex gap-1 rounded-lg border border-white/10 bg-stage-900/70 p-1 backdrop-blur" />
        <UserMenu
          name={account.profiles[0]?.display_name ?? account.email}
          profileId={account.profiles[0]?.id ?? account.account_id}
          avatar={account.profiles[0]?.avatar ?? null}
          // A hint, never a check: `/admin` is reachable by anybody and the
          // server answers 404 to everybody else (phase 13d). Hiding the link
          // is so a player is not offered a screen with nothing in it.
          curator={account.role === "admin"}
          onSignOut={() => {
            // `refresh` on both paths: a logout that failed server-side still
            // has to re-read the session, or the menu keeps showing somebody
            // who may no longer be signed in.
            void signOut().then(refresh, refresh);
          }}
        />
      </div>

      <main className="relative mx-auto flex min-h-full w-full max-w-5xl flex-col items-center justify-center gap-12 px-5 py-16">
        <header className="flex flex-col items-center text-center">
          <Logo />
          {/* The name, small — the mark above it is the big element. `h1` because
              it is what this page *is*; the sentence below is larger on screen,
              which is a visual hierarchy and not a heading one. */}
          <h1 className="wordmark mt-4 text-2xl font-bold tracking-tight">
            {t("app.name")}
          </h1>
        </header>

        <Explainer />

        {/* Join first in the source, so it is first for a screen reader and
            first when these stack on a phone. */}
        <div className="grid w-full gap-5 md:grid-cols-2">
          <JoinPanel />
          <StartPanel />
        </div>
      </main>
    </div>
  );
}

/** What the game is, for somebody who has never heard of it. */
function Explainer() {
  const { t } = useTranslation();

  const steps = [
    { key: "listen", ordinal: "1" },
    { key: "name", ordinal: "2" },
    { key: "place", ordinal: "3" },
  ] as const;

  return (
    <section className="flex w-full flex-col items-center gap-8 text-center">
      <div className="flex flex-col items-center gap-3">
        <p className="max-w-2xl text-balance text-3xl font-semibold leading-tight sm:text-4xl">
          {t("home.headline")}
        </p>
        <p className="max-w-xl text-pretty text-base leading-relaxed text-white/60">
          {t("home.blurb")}
        </p>
      </div>

      {/* An ordered list because the steps are a sequence, and a player who
          hears them out of order has been told the wrong game. */}
      <ol className="grid w-full gap-4 sm:grid-cols-3">
        {steps.map(({ key, ordinal }) => (
          <li
            key={key}
            className="flex flex-col items-center gap-2 rounded-xl border border-white/10 bg-stage-900/50 p-5 backdrop-blur-sm"
          >
            <span className="flex size-8 items-center justify-center rounded-full bg-flash-500/15 text-sm font-bold text-flash-400">
              {ordinal}
            </span>
            <h2 className="text-base font-semibold">{t(`home.steps.${key}.title`)}</h2>
            <p className="text-sm leading-relaxed text-white/55">
              {t(`home.steps.${key}.body`)}
            </p>
          </li>
        ))}
      </ol>

      <p className="text-sm font-medium uppercase tracking-[0.2em] text-flash-400">
        {t("home.win")}
      </p>
    </section>
  );
}

/** The shared shell the two big panels sit in. */
function Panel({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={
        "flex flex-col gap-4 rounded-2xl border border-white/10 bg-stage-900/70 p-6 backdrop-blur " +
        (className ?? "")
      }
    >
      {children}
    </section>
  );
}

/** The code box. The whole point of the app for most of the people who use it. */
function JoinPanel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const room = code.trim().toUpperCase();

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    void (async () => {
      try {
        // Checked here so a mistyped code fails on the screen that has the box,
        // rather than on the party page, which would have to explain a room
        // that was never there.
        const party = await previewParty(room);
        if (!party) {
          setError(t("home.join.unknownCode", { code: room }));
          setBusy(false);
          return;
        }
        void navigate(`/party/${party.code}`);
      } catch {
        // The server's own message is not translated and is written for an
        // operator; a guest gets the one sentence that is true either way.
        setError(t("home.join.unreachable"));
        setBusy(false);
      }
    })();
  };

  return (
    <Panel className="border-flash-500/25 ring-1 ring-flash-500/10">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-semibold">{t("home.join.title")}</h2>
          <p className="text-sm text-white/55">{t("home.join.lead")}</p>
        </div>
        <Field
          id="party-code"
          label={t("home.join.label")}
          value={code}
          onChange={setCode}
          // The alphabet has no I, O, 1 or 0 precisely so it survives being read
          // aloud across a room — and lowercase is fine, the server normalises.
          autoCapitalize="characters"
          autoComplete="off"
          maxLength={4}
          required
          placeholder="BKGZ"
          hint={t("home.join.hint")}
          // The one field on this page somebody types four characters into from
          // across a room, so it gets the treatment: big, spaced and centred.
          className="text-center font-mono text-3xl uppercase tracking-[0.5em] placeholder:tracking-[0.5em]"
        />
        {error && <ErrorLine>{error}</ErrorLine>}
        <Action disabled={busy || room.length !== 4}>{t("home.join.action")}</Action>
      </form>
    </Panel>
  );
}

/**
 * Starting a party, for somebody signed in.
 *
 * It used to carry a line of grey text — "Signed in as … · your career · sign
 * out" — which was the only route to either. That is the user menu's job now,
 * top right, where people look for it. This panel does one thing.
 */
function StartPanel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [partyName, setPartyName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    void (async () => {
      try {
        // Creating seats the host in the same transaction and sets the device
        // cookie, so the lobby is one navigation away with no join step.
        const seat = await createParty(
          partyName.trim() ? { name: partyName.trim() } : {},
        );
        void navigate(`/party/${seat.party.code}`);
      } catch {
        setError(t("home.start.failed"));
        setBusy(false);
      }
    })();
  };

  return (
    <Panel>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <h2 className="text-xl font-semibold">{t("home.start.title")}</h2>
        <Field
          id="party-name"
          label={t("home.start.nameLabel")}
          value={partyName}
          onChange={setPartyName}
          autoComplete="off"
          maxLength={60}
          placeholder={t("home.start.namePlaceholder")}
          hint={t("home.start.nameHint")}
        />
        {error && <ErrorLine>{error}</ErrorLine>}
        <Action disabled={busy}>{t("home.start.action")}</Action>
      </form>
    </Panel>
  );
}
